import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, StaleAuthError } from '../api/client';
import { useScreenRequest } from '../hooks/useScreenRequest';
import { useAnnounceOnce } from './PageStates';

/**
 * #583 S6b2, design §7.5 and §7.6: inline confirmations in place of prompt() and confirm(). One
 * form at a time, one request per confirmation, and every answer ends in one sentence and a
 * re-read of the record. A mutation is never retried or aborted here (client.js owns that).
 *
 * action: { key, label, className, title, about, field?: 'reason' | 'password', submit,
 *           send(value, options), done, refused?(err) -> sentence, unknown }
 */

// server/schemas/platform-user.schemas.js passwordBody: 8 to 72 characters and at most 72 UTF-8 bytes.
export const PASSWORD_RULE = '8 to 72 characters, and at most 72 bytes: some letters and symbols count as 2 to 4 bytes.';
const fitOf = (pw) => (pw.length < 8 ? 'short' : new TextEncoder().encode(pw).length > 72 ? 'long' : 'ok');
// server/schemas/platform-operator.schemas.js reasonBody: trimmed, 1 to 500 characters.
const REASON_MAX = 500;

const REFUSED = {
  401: 'your operator session ended.',
  429: 'too many requests from this network. Try again in about 15 minutes.',
};
// Nothing happened, and the same form can be sent again once corrected or after a wait.
const KEEPS_FORM = [400, 429];

/** Only a 4xx is the server refusing. A 5xx may come from a proxy after a commit; a lost answer says nothing. */
function outcomeOf(action, answer, err) {
  if (!err) {
    const auditId = answer?.auditId ?? null;
    return { tone: 'done', auditId, text: `${action.done}${auditId ? ` Audit entry ${auditId}.` : ' The server returned no audit entry.'}` };
  }
  if (err instanceof ApiError && err.status < 500) {
    return { tone: 'refused', needsRead: err.status === 409, text: `Not changed: ${action.refused?.(err) ?? REFUSED[err.status] ?? 'the server refused this action.'}` };
  }
  return {
    tone: 'unknown', needsRead: true,
    text: `Outcome unknown: the server did not confirm whether ${action.unknown}.`,
  };
}

let outcomes = 0;

/**
 * `disabled` holds every action, e.g. while the record is loading or stale. `onDone` re-reads it;
 * `refreshing` is true while that re-read is in flight. An outcome is spoken only once the re-read
 * has landed, so the live region never holds "Refreshing" and the outcome together (fix round 1, M4).
 */
export default function RecordActions({ actions, disabled, refreshing, readSucceeded, isFresh, onDone }) {
  const { isCurrentScreen } = useScreenRequest('record-actions');
  const [openKey, setOpenKey] = useState(null);
  const [pending, setPending] = useState(false);
  // What the field's value says about the server rule. The value itself is never held in state.
  const [fit, setFit] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [fieldRefusal, setFieldRefusal] = useState(null);
  if (outcome && !outcome.ready && !refreshing) {
    const readResult = readSucceeded
      ? ' The page now shows the current state; check the audit log before trying again.'
      : ' The page could not re-read the record; check the audit log before trying again.';
    setOutcome({ ...outcome, ready: true, text: outcome.text + (outcome.needsRead ? readResult : '') });
  }
  const busy = useRef(false);
  const root = useRef(null);
  const openers = useRef({});
  const field = useRef(null);
  const heading = useRef(null);
  const form = useRef(null);
  const ids = useId();
  useAnnounceOnce(outcome?.ready ? outcome.text : null, outcome?.id);
  const action = actions.find((a) => a.key === openKey);

  // The page heading is always mounted: focus goes there when its own place is gone.
  const toHeading = () => {
    const title = root.current?.closest('main')?.querySelector('h2');
    if (title) { title.tabIndex = -1; title.focus(); }
  };

  useEffect(() => {
    if (openKey) (field.current || heading.current)?.focus();
  }, [openKey]);

  // Fix round 1, H1: a re-read can change the record under an open form (lock -> unlocked), so its
  // action no longer exists and the form has just been removed. Focus must not stay on <body>.
  const orphaned = Boolean(openKey && !action);
  useLayoutEffect(() => {
    if (!orphaned) return;
    setOpenKey(null);
    if (!document.activeElement || document.activeElement === document.body) toHeading();
  }, [orphaned]);

  const show = (a) => {
    setOpenKey(a.key);
    setFit(a.field ? null : 'ok');
    setFieldRefusal(null);
  };
  // Fix round 1, H1: a held record disables its openers, and a disabled button cannot take focus.
  const cancel = () => {
    const opener = openers.current[openKey];
    setOpenKey(null);
    if (opener?.isConnected && !opener.disabled) opener.focus();
    else toHeading();
  };

  async function submit(event) {
    event.preventDefault();
    if (busy.current || disabled || !isFresh() || fit !== 'ok') return;
    busy.current = true;
    const value = field.current?.value;
    if (action.field === 'password' && field.current) field.current.value = '';
    // Focus leaves the submit before it disables itself, so it never drops to <body>.
    heading.current.focus();
    setPending(true);
    setOutcome(null);
    setFieldRefusal(null);
    let answer;
    let err = null;
    try {
      answer = await action.send(value, { isCurrentRequest: isCurrentScreen });
    } catch (e) {
      err = e;
    }
    if (action.field === 'password' && field.current) field.current.value = '';
    busy.current = false;
    // A stale answer belongs to nobody on this screen; the session owner reports its outcome.
    // The form is released only while the screen is still mounted (fix round 1, L2).
    if (err instanceof StaleAuthError || !isCurrentScreen()) {
      if (isCurrentScreen()) setPending(false);
      return;
    }
    setPending(false);
    outcomes += 1;
    setOutcome({ ...outcomeOf(action, answer, err), id: outcomes, ready: false });
    if (err instanceof ApiError && KEEPS_FORM.includes(err.status)) {
      setFieldRefusal(openKey);
      if (action.field === 'password') setFit(null);
      field.current?.focus();
    } else {
      if (form.current?.contains(document.activeElement)) toHeading();
      setOpenKey(null);
    }
    if (err?.status !== 401) onDone();
  }

  const hint = `${ids}hint`;
  const tooLong = `${ids}long`;
  const receiptId = `${ids}outcome`;
  const refusedField = fieldRefusal !== null && fieldRefusal === openKey;
  const description = [hint, refusedField ? receiptId : null].filter(Boolean).join(' ');
  return (
    <div className="record-actions" ref={root}>
      <div className="action-bar">
        {actions.map((a) => (
          <button key={a.key} type="button" className={`btn ${a.className}`} ref={(node) => { openers.current[a.key] = node; }}
            aria-expanded={openKey === a.key} disabled={disabled || pending} onClick={() => show(a)}>{a.label}</button>
        ))}
      </div>
      {action && (
        <form ref={form} className="action-panel card" aria-labelledby={`${ids}title`} onSubmit={submit} noValidate>
          <h3 id={`${ids}title`} ref={heading} tabIndex={-1}>{action.title}</h3>
          <p>{action.about}</p>
          {action.field === 'reason' && (
            <div className="field">
              <label htmlFor={`${ids}field`}>Reason (required)</label>
              <textarea id={`${ids}field`} ref={field} rows={3} maxLength={REASON_MAX} aria-invalid={refusedField} aria-describedby={description}
                onChange={(e) => { setFieldRefusal(null); setFit(e.target.value.trim() ? 'ok' : null); }} />
              <p id={hint} className="field-hint">Recorded in the audit log with this action. 1 to {REASON_MAX} characters.</p>
            </div>
          )}
          {action.field === 'password' && (
            <div className="field">
              <label htmlFor={`${ids}field`}>New password</label>
              <input id={`${ids}field`} ref={field} type="password" autoComplete="new-password" aria-describedby={fit === 'long' ? `${description} ${tooLong}` : description}
                aria-invalid={refusedField || fit === 'long'} onChange={(e) => { setFieldRefusal(null); setFit(e.target.value ? fitOf(e.target.value) : null); }} />
              <p id={hint} className="field-hint">{PASSWORD_RULE}</p>
              {fit === 'long' && <p id={tooLong} className="field-error">Too long: at most 72 bytes.</p>}
            </div>
          )}
          <div className="action-bar">
            <button type="submit" className={`btn ${action.className}`} disabled={pending || disabled || fit !== 'ok'}>
              {pending ? 'Sending…' : action.submit}
            </button>
            <button type="button" className="btn btn-outline" disabled={pending} onClick={cancel}>Cancel</button>
          </div>
        </form>
      )}
      {outcome && (
        <div className={`action-outcome action-${outcome.tone}`}>
          <p id={receiptId}>{outcome.text}</p>
          {outcome.auditId && <Link className="btn btn-outline" to={`/audit/${outcome.auditId}`}>View audit entry {outcome.auditId}</Link>}
          {outcome.tone === 'unknown' && <Link className="btn btn-outline" to="/audit">Open the audit log</Link>}
        </div>
      )}
    </div>
  );
}
